let searchLastInputWasPointer = false;
document.addEventListener('pointerdown', () => { searchLastInputWasPointer = true; }, true);
document.addEventListener('keydown', () => { searchLastInputWasPointer = false; }, true);

class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.id = this.getAttribute('id')
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');
    
    if (this.input) {
      this.input.addEventListener('pointerdown', () => this.input.classList.add('focus-quiet'));
      this.input.addEventListener('blur', () => this.input.classList.remove('focus-quiet'));
      this.input?.form?.addEventListener('reset', this.onFormReset.bind(this));
      this.input?.addEventListener(
        'input',
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );
    }
  }

  toggleResetButton() {
    if (!this.resetButton) return;
    const resetIsHidden = this.resetButton.classList.contains('hidden');
    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove('hidden');
    } else if (this.input.value.length === 0 && !resetIsHidden) {
      this.resetButton.classList.add('hidden');
    }
  }

  onChange() {
    this.toggleResetButton();
  }

  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a');
  }

  onFormReset(event) {
    // Prevent default so the form reset doesn't set the value gotten from the url on page load
    event.preventDefault();
    // Don't reset if the user has selected an element on the predictive search dropdown
    if (this.shouldResetForm()) {
      this.input.value = '';
      this.input.focus();
      this.toggleResetButton();
    }
  }
}

customElements.define('search-form', SearchForm);

class SearchModal extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.modal = this.closest('modal-dialog');
    this.closeButton = this.modal?.querySelector('[data-modal-close]');
    this.predictiveSearch = this.closest('predictive-search');
    
    // CRITICAL: Disable PredictiveSearch keyboard handling for modal
    this.disablePredictiveSearchKeyboard();
    
    // Initialize our own keyboard navigation
    this.initKeyboardNavigation();
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      // this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    // this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      // this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    // this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      // this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    let URL = `${window.Shopify.routes.root}search/suggest.json?q=${encodeURIComponent(searchTerm)}`
    
    fetch(URL)
      .then((response) => response.json())
      .then((suggestions) => {
        const productSuggestions = suggestions.resources.results.products;
      }
    );
  }

  // ========== DISABLE PREDICTIVE SEARCH KEYBOARD ==========
  
  disablePredictiveSearchKeyboard() {
    if (!this.predictiveSearch) return;

    // Store reference to original onKeydown
    const originalOnKeydown = this.predictiveSearch.onKeydown;
    
    // Override PredictiveSearch's onKeydown to allow Tab always
    this.predictiveSearch.onKeydown = (event) => {
      // If it's Tab, don't let PredictiveSearch handle it
      if (event.key === 'Tab') {
        return; // Don't call original handler
      }
      
      // For other keys, call the original handler
      if (originalOnKeydown) {
        originalOnKeydown.call(this.predictiveSearch, event);
      }
    };

  }

  // ========== KEYBOARD NAVIGATION ==========
  
  initKeyboardNavigation() {
    if (!this.modal) return;

    // Listen for modal open event
    this.modal.addEventListener('modalChanged', (e) => {
      if (e.detail.show) {
        this.onModalOpen();
      }
    });

    // Add Tab key handler for focus trap
    this.modal.addEventListener('keydown', this.handleTabKey.bind(this));

    // Add keyboard handlers to recommendation links
    this.addKeyboardToRecommendations();
  }

  onModalOpen() {
    // Focus search input when modal opens
    setTimeout(() => {
      if (this.input) {
        if (searchLastInputWasPointer) this.input.classList.add('focus-quiet');
        this.input.focus();
      }
    }, 100);
  }

  getFocusableElements() {
    if (!this.modal) return [];
    
    return Array.from(
      this.modal.querySelectorAll(
        'input:not([type="hidden"]):not([disabled]), ' +
        'button:not([disabled]), ' +
        'a[href]:not([disabled]), ' +
        '[tabindex="0"]'
      )
    ).filter(el => {
      // Filter out hidden elements
      const style = window.getComputedStyle(el);
      const isVisible = style.display !== 'none' && 
                       style.visibility !== 'hidden' &&
                       el.offsetParent !== null;
      
      // Special case: input might be visible but offsetParent null
      return isVisible || el === this.input;
    });
  }

  handleTabKey(e) {
    if (e.key !== 'Tab') return;

    const focusableElements = this.getFocusableElements();
    
    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    const activeElement = document.activeElement;

    // Shift + Tab on first element -> go to last
    if (e.shiftKey && activeElement === firstElement) {
      e.preventDefault();
      lastElement.focus();
    }
    // Tab on last element -> go to first
    else if (!e.shiftKey && activeElement === lastElement) {
      e.preventDefault();
      firstElement.focus();
    }
    // Otherwise let Tab work naturally
  }

  addKeyboardToRecommendations() {
    if (!this.modal) return;

    // Add Enter/Space handlers to recommendation links
    const recommendations = this.modal.querySelectorAll('.predictive-search__item');
    
    recommendations.forEach(link => {
      link.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          link.click();
        }
      });
    });
  }

  // Re-initialize keyboard handlers when search results update
  renderSearchResults(results) {
    // Your existing render logic here
    
    // Re-add keyboard handlers to new elements
    this.addKeyboardToRecommendations();
  }
}

if (!customElements.get('search-modal')) { 
  customElements.define('search-modal', SearchModal); 
}